import { getSupportedCountryCurrency } from '@change-corgi/config/currency';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { MembershipBottomBannerFeatureConfigFieldsFragment } from './membershipBottomBannerFeatureConfigFields.graphql';
import type { MembershipBottomBannerData } from './types';

type I18n = Pick<UtilityContext['i18n'], 'amountFromBaseUnits' | 'localizeCurrency'>;

function mapAmountToSubscriptionAmount(
	amount: number,
	currency: string,
	{ amountFromBaseUnits, localizeCurrency }: I18n,
) {
	const amountInCurrency = amountFromBaseUnits(amount, currency);
	return {
		amount: amountInCurrency,
		amountLocalized: localizeCurrency(amountInCurrency, currency, {
			currencyDisplay: 'narrowSymbol',
			amountInBaseUnits: false,
		}),
	};
}

/**
 * returns null when the banner is disabled or its subscription configuration is missing,
 * so that the banner component only ever receives complete data
 */
export function normalizeMembershipBottomBannerData(
	{
		membershipBottomBannerEnabled,
		subscriptionConfiguration,
		showOneTimeContributions,
		yearlyContributionsAmountsConfig,
	}: MembershipBottomBannerFeatureConfigFieldsFragment,
	{ countryCode, i18n }: { countryCode: string; i18n: I18n },
): MembershipBottomBannerData | null {
	if (!membershipBottomBannerEnabled || !subscriptionConfiguration) return null;

	const currency = getSupportedCountryCurrency(countryCode) || 'USD';

	return {
		currency,
		subscriptionConfig: {
			amounts: subscriptionConfiguration.amounts.map((amount) => mapAmountToSubscriptionAmount(amount, currency, i18n)),
			defaultAmount: mapAmountToSubscriptionAmount(subscriptionConfiguration.defaultAmount, currency, i18n),
		},
		featureConfigs: { showOneTimeContributions, yearlyContributionsAmountsConfig },
	};
}
